// Updated StripeSubscription.js with token handling fix

import React, { useState, useEffect } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";
import API_BASE_URL from "./../common/config";
import {
  ErrorNotification,
  SuccessNotification,
} from "../tosterNotification/tosters";

const StripeSubscription = ({ planDetails, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    const createPaymentIntent = async () => {
      // Enhanced validation
      if (!planDetails || !planDetails.name || !planDetails.priceId) {
        ErrorNotification("Invalid plan details. Please select a valid plan.");
        console.error("Invalid planDetails:", planDetails);
        return;
      }

      try {
        setLoading(true);

        // Log the exact plan details for debugging
        console.log("Sending plan details to server:", {
          planName: planDetails.name,
          priceId: planDetails.priceId,
          price: planDetails.price,
        });

        // Make request without token authentication
        const response = await axios.post(
          `${API_BASE_URL}/payment/create-subscription`,
          {
            planName: planDetails.name,
            priceId: planDetails.priceId,
            // Add customer info if available
            customerEmail: planDetails.customerEmail,
            customerName: planDetails.customerName,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data && response.data.success) {
          setClientSecret(response.data.clientSecret);
        } else {
          // Enhanced error handling
          const errorMessage =
            response.data?.message || "Failed to initialize payment";
          console.error("Payment intent creation failed:", response.data);
          ErrorNotification(errorMessage);

          // If server specifically indicates we should retry
          if (response.data?.retry) {
            setTimeout(createPaymentIntent, 2000); // Retry after 2 seconds
          }
        }
      } catch (error) {
        console.error("Error creating payment intent:", error);
        // Show more detailed error if available
        const errorMessage =
          error.response?.data?.message ||
          "Failed to initialize payment system";
        ErrorNotification(errorMessage);

        // Automatic retry for network errors
        if (!error.response) {
          setTimeout(createPaymentIntent, 3000); // Retry after 3 seconds on network error
        }
      } finally {
        setLoading(false);
      }
    };

    createPaymentIntent();
  }, [planDetails]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    setLoading(true);

    try {
      const cardElement = elements.getElement(CardElement);

      // Get billing details from planDetails if available
      const billingDetails = {
        name: planDetails.customerName || "Customer",
        email: planDetails.customerEmail || "",
      };

      // Confirm the payment
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        }
      );

      if (error) {
        console.error("[Payment Error]", error);
        ErrorNotification(error.message || "Payment failed");
      } else if (paymentIntent.status === "succeeded") {
        // Payment successful
        SuccessNotification(
          "Payment successful! Activating your subscription..."
        );

        // Call the success callback with complete payment info
        if (onSuccess) {
          onSuccess({
            paymentId: paymentIntent.id,
            plan: planDetails.name,
            amount: planDetails.price,
            currency: "INR",
            status: paymentIntent.status,
            priceId: planDetails.priceId,
          });
        }
      } else {
        ErrorNotification(
          `Payment status: ${paymentIntent.status}. Please try again.`
        );
      }
    } catch (error) {
      console.error("Payment submission error:", error);
      ErrorNotification("An error occurred processing your payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stripe-payment-form">
      <h4 className="text-center mb-4">Complete Your Subscription</h4>
      <p className="text-center mb-4">
        Subscribe to {planDetails?.name || "Plan"} -{" "}
        {planDetails?.price || "₹0"}/{planDetails?.duration || "month"}
      </p>

      <form onSubmit={handleSubmit}>
        <div className="card-element-container mb-4">
          <label className="form-label">Card Details</label>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
              hidePostalCode: true, // Remove postal code field
            }}
          />
        </div>

        <div className="payment-message mb-3">
          <small className="text-muted">
            This is a secure payment processed by Stripe. Your card details are
            never stored on our servers.
          </small>
        </div>

        <div className="d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={!stripe || loading || !clientSecret}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Processing...
              </>
            ) : (
              `Pay ${planDetails?.price || "₹0"}`
            )}
          </button>
        </div>
      </form>

      <style jsx="true">{`
        .stripe-payment-form {
          padding: 20px;
          border-radius: 8px;
          background-color: #fff;
        }

        .card-element-container {
          border: 1px solid #ced4da;
          padding: 15px;
          border-radius: 5px;
          background-color: #fafafa;
        }

        .payment-message {
          text-align: center;
          color: #6c757d;
        }

        .btn-primary {
          background: linear-gradient(135deg, #7928ca, #6600cc);
          border: none;
          box-shadow: 0 4px 12px rgba(102, 0, 204, 0.3);
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 500;
          letter-spacing: 0.5px;
          transition: all 0.3s ease;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #8a3cd0, #7211d8);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(102, 0, 204, 0.35);
        }

        .btn-primary:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .btn-secondary {
          border: 1px solid #6c757d;
          background-color: white;
          color: #6c757d;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .btn-secondary:hover:not(:disabled) {
          background-color: #f8f9fa;
          color: #5a6268;
        }
      `}</style>
    </div>
  );
};

export default StripeSubscription;
